import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Calendar, Users, Building2, TrendingUp, AlertCircle, X } from 'lucide-react';
import useHRMetrics from '../../hooks/useHRMetrics';
import MetricTile from '../recruiting/MetricTile';
import MetricDetailPane from '../recruiting/MetricDetailPane';
import ErrorBoundary from '../shared/ErrorBoundary';

/**
 * HROverviewTab - Onboarding Pipeline Dashboard
 * 
 * Features:
 * - 4 pipeline tiles (Offer Extended → Onboarded)
 * - Time scaler (7d, 30d, 90d, All)
 * - Client filter
 * - Account Manager filter
 * - At-risk candidates alert
 * - Onboarding breakdown by client/AM
 */
const HROverviewTabContent = ({ 
  submissions = [], 
  jobs = [], 
  candidates = [], 
  users = [],
  clients = []
}) => {
  const [selectedTimeframe, setSelectedTimeframe] = useState('this_month');
  const [selectedClients, setSelectedClients] = useState(new Set());
  const [selectedAccountManagers, setSelectedAccountManagers] = useState(new Set());
  const [selectedMetric, setSelectedMetric] = useState(null);
  const [clientSearchTerm, setClientSearchTerm] = useState('');
  const [amSearchTerm, setAmSearchTerm] = useState('');
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const [showAmDropdown, setShowAmDropdown] = useState(false);
  
  const clientDropdownRef = useRef(null);
  const amDropdownRef = useRef(null);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (clientDropdownRef.current && !clientDropdownRef.current.contains(event.target)) {
        setShowClientDropdown(false);
      }
      if (amDropdownRef.current && !amDropdownRef.current.contains(event.target)) {
        setShowAmDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Time frame options (matching recruiter dashboard)
  const timeframeOptions = [
    { value: 'ytd', label: 'YTD' },
    { value: 'this_month', label: 'This Month' },
    { value: 'last_month', label: 'Last Month' },
    { value: 'this_year', label: 'This Year' },
    { value: 'last_year', label: 'Last Year' },
    { value: 'last_6_months', label: 'Last 6 Months' },
    { value: 'last_12_months', label: 'Last 12 Months' }
  ];

  // Get metrics using the hook
  const {
    onboardingPipelineMetrics,
    atRiskCandidates,
    onboardingByClient,
    onboardingByAM
  } = useHRMetrics({
    submissions,
    jobs,
    candidates,
    users,
    selectedTimeframe,
    selectedClients,
    selectedAccountManagers
  });

  // Get unique clients from jobs
  const uniqueClients = useMemo(() => {
    const clientSet = new Set();
    jobs.forEach(job => {
      if (job.client_company) clientSet.add(job.client_company);
    });
    return Array.from(clientSet).sort();
  }, [jobs]);

  // Get unique Account Managers from jobs
  const uniqueAccountManagers = useMemo(() => {
    const amMap = new Map();
    jobs.forEach(job => {
      const amId = job.account_manager_id || job.assigned_account_manager;
      if (amId) {
        const am = users.find(u => u.id === amId);
        if (am) {
          amMap.set(amId, am.full_name || am.username);
        }
      }
    });
    return Array.from(amMap.entries()).map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [jobs, users]);

  // Handle client filter toggle
  const toggleClient = (client) => {
    const newSet = new Set(selectedClients);
    if (newSet.has(client)) {
      newSet.delete(client);
    } else {
      newSet.add(client);
    }
    setSelectedClients(newSet);
  };

  // Handle AM filter toggle
  const toggleAccountManager = (amId) => {
    const newSet = new Set(selectedAccountManagers);
    if (newSet.has(amId)) {
      newSet.delete(amId);
    } else {
      newSet.add(amId);
    }
    setSelectedAccountManagers(newSet);
  };

  return (
    <div className="space-y-6">
      {/* Filters Bar */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex items-center flex-wrap gap-4">
          {/* Timeframe Selector */}
          <div className="flex items-center gap-2 flex-wrap">
            <Calendar className="h-4 w-4 text-gray-500" />
            <span className="text-sm font-medium text-gray-700">Period:</span>
            <div className="flex gap-2 flex-wrap">
              {timeframeOptions.map(option => (
                <button
                  key={option.value}
                  onClick={() => setSelectedTimeframe(option.value)}
                  className={`px-3 py-1 text-xs font-medium rounded transition-colors ${
                    selectedTimeframe === option.value
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          {/* Client Filter Searchable Dropdown */}
          <div className="flex items-center gap-2">
            <Building2 className="h-4 w-4 text-gray-500" />
            <span className="text-sm font-medium text-gray-700">Client:</span>
            <div className="relative" ref={clientDropdownRef}>
              <input
                type="text"
                value={selectedClients.size === 1 ? Array.from(selectedClients)[0] : clientSearchTerm}
                onChange={(e) => {
                  setClientSearchTerm(e.target.value);
                  setSelectedClients(new Set());
                  setShowClientDropdown(true);
                }}
                onFocus={() => setShowClientDropdown(true)}
                placeholder={`Search ${uniqueClients.length} clients...`}
                className="w-64 px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              {showClientDropdown && (
                <div className="absolute z-50 mt-1 w-full bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                  <div
                    className="px-3 py-2 hover:bg-gray-100 cursor-pointer text-sm"
                    onClick={() => {
                      setSelectedClients(new Set());
                      setClientSearchTerm('');
                      setShowClientDropdown(false);
                    }}
                  >
                    <strong>All Clients ({uniqueClients.length})</strong>
                  </div>
                  {uniqueClients
                    .filter(client => client.toLowerCase().includes(clientSearchTerm.toLowerCase()))
                    .map(client => (
                      <div
                        key={client}
                        className="px-3 py-2 hover:bg-blue-50 cursor-pointer text-sm"
                        onClick={() => {
                          setSelectedClients(new Set([client]));
                          setClientSearchTerm('');
                          setShowClientDropdown(false);
                        }}
                      >
                        {client}
                      </div>
                    ))}
                </div>
              )}
            </div>
          </div>

          {/* Account Manager Filter Searchable Dropdown */}
          <div className="flex items-center gap-2">
            <Users className="h-4 w-4 text-gray-500" />
            <span className="text-sm font-medium text-gray-700">Account Manager:</span>
            <div className="relative" ref={amDropdownRef}>
              <input
                type="text"
                value={selectedAccountManagers.size === 1 
                  ? (uniqueAccountManagers.find(am => am.id === Array.from(selectedAccountManagers)[0])?.name || '') 
                  : amSearchTerm}
                onChange={(e) => {
                  setAmSearchTerm(e.target.value);
                  setSelectedAccountManagers(new Set());
                  setShowAmDropdown(true);
                }}
                onFocus={() => setShowAmDropdown(true)}
                placeholder={`Search ${uniqueAccountManagers.length} AMs...`}
                className="w-64 px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              {showAmDropdown && (
                <div className="absolute z-50 mt-1 w-full bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                  <div
                    className="px-3 py-2 hover:bg-gray-100 cursor-pointer text-sm"
                    onClick={() => {
                      setSelectedAccountManagers(new Set());
                      setAmSearchTerm('');
                      setShowAmDropdown(false);
                    }}
                  >
                    <strong>All AMs ({uniqueAccountManagers.length})</strong>
                  </div>
                  {uniqueAccountManagers
                    .filter(am => am.name.toLowerCase().includes(amSearchTerm.toLowerCase()))
                    .map(am => (
                      <div
                        key={am.id}
                        className="px-3 py-2 hover:bg-blue-50 cursor-pointer text-sm"
                        onClick={() => {
                          setSelectedAccountManagers(new Set([am.id]));
                          setAmSearchTerm('');
                          setShowAmDropdown(false);
                        }}
                      >
                        {am.name}
                      </div>
                    ))}
                </div>
              )}
            </div>
          </div>

          {/* Clear Filters */}
          {(selectedClients.size > 0 || selectedAccountManagers.size > 0) && (
            <button
              onClick={() => {
                setSelectedClients(new Set());
                setSelectedAccountManagers(new Set());
              }}
              className="ml-auto text-sm text-blue-600 hover:text-blue-700 font-medium"
            >
              Clear Filters
            </button>
          )}
        </div>
      </div>

      {/* At Risk Alert */}
      {atRiskCandidates.count > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-red-600 mt-0.5 flex-shrink-0" />
          <div>
            <h4 className="text-sm font-semibold text-red-900">
              ⚠️ Urgent: {atRiskCandidates.count} Candidate{atRiskCandidates.count !== 1 ? 's Need' : ' Needs'} Immediate Attention
            </h4>
            <p className="text-sm text-red-700 mt-1">
              {atRiskCandidates.count === 1 ? 'This candidate has' : 'These candidates have'} been in "Offer Accepted" status for more than 7 days without onboarding initiated. 
              <strong> Immediate action required</strong> to prevent candidate dropout.
            </p>
          </div>
        </div>
      )}

      {/* Onboarding Pipeline Tiles */}
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <div className="h-1 w-12 bg-gradient-to-r from-orange-500 to-green-500 rounded-full"></div>
          <h4 className="text-lg font-semibold text-gray-900">Onboarding Pipeline</h4>
          <span className="text-sm text-gray-500">
            ({timeframeOptions.find(opt => opt.value === selectedTimeframe)?.label || 'This Month'})
          </span>
        </div>
        
        <div className="grid grid-cols-3 gap-6">
          {Object.entries(onboardingPipelineMetrics).map(([key, metric]) => (
            <MetricTile
              key={key}
              metric={metric}
              metricKey={key}
              isSelected={selectedMetric === key}
              onSelect={(k) => setSelectedMetric(selectedMetric === k ? null : k)}
              testId={`hr-metric-${key}`}
            />
          ))}
        </div>
      </div>

      {/* Metric Detail Pane */}
      {selectedMetric && onboardingPipelineMetrics[selectedMetric] && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-2xl font-bold text-gray-900">
                {onboardingPipelineMetrics[selectedMetric].label}
              </h2>
              <button
                onClick={() => setSelectedMetric(null)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="h-6 w-6 text-gray-500" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-80px)]">
              <MetricDetailPane
                metric={onboardingPipelineMetrics[selectedMetric]}
                metricKey={selectedMetric}
                submissions={onboardingPipelineMetrics[selectedMetric].data}
                candidates={candidates}
                jobs={jobs}
              />
            </div>
          </div>
        </div>
      )}

      {/* Onboarding by Client */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Building2 className="h-5 w-5 text-blue-600" />
          Onboarding by Client
        </h3>
        <div className="space-y-3">
          {onboardingByClient.slice(0, 10).map(client => (
            <div key={client.name} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 bg-blue-600 rounded-lg flex items-center justify-center text-white font-semibold text-sm">
                  {client.name.substring(0, 2).toUpperCase()}
                </div>
                <div>
                  <p className="font-medium text-gray-900">{client.name}</p>
                  <p className="text-xs text-gray-500">{client.total} total</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="text-center">
                  <p className="text-sm font-semibold text-blue-600">{client.offerAccepted}</p>
                  <p className="text-xs text-gray-500">Accepted</p>
                </div>
                <div className="text-center">
                  <p className="text-sm font-semibold text-orange-600">{client.onboardingInitiated}</p>
                  <p className="text-xs text-gray-500">In Progress</p>
                </div>
                <div className="text-center">
                  <p className="text-sm font-semibold text-green-600">{client.onboarded}</p>
                  <p className="text-xs text-gray-500">Completed</p>
                </div>
              </div>
            </div>
          ))}
          {onboardingByClient.length === 0 && (
            <p className="text-center text-gray-500 py-4">No onboarding data available</p>
          )}
        </div>
      </div>

      {/* Onboarding by Account Manager */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Users className="h-5 w-5 text-blue-600" />
          Onboarding by Account Manager
        </h3>
        <div className="space-y-3">
          {onboardingByAM.slice(0, 10).map(am => (
            <div key={am.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 bg-indigo-600 rounded-full flex items-center justify-center text-white font-semibold text-sm">
                  {am.name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase()}
                </div>
                <div>
                  <p className="font-medium text-gray-900">{am.name}</p>
                  <p className="text-xs text-gray-500">{am.total} total</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="text-center">
                  <p className="text-sm font-semibold text-blue-600">{am.offerAccepted}</p>
                  <p className="text-xs text-gray-500">Accepted</p>
                </div>
                <div className="text-center">
                  <p className="text-sm font-semibold text-orange-600">{am.onboardingInitiated}</p>
                  <p className="text-xs text-gray-500">In Progress</p>
                </div>
                <div className="text-center">
                  <p className="text-sm font-semibold text-green-600">{am.onboarded}</p>
                  <p className="text-xs text-gray-500">Completed</p>
                </div>
              </div>
            </div>
          ))}
          {onboardingByAM.length === 0 && (
            <p className="text-center text-gray-500 py-4">No onboarding data available</p>
          )}
        </div>
      </div>
    </div>
  );
};

// Wrap with Error Boundary
const HROverviewTab = (props) => (
  <ErrorBoundary
    title="HR Overview Error"
    message="Unable to load the HR overview. This might be due to a temporary issue."
  >
    <HROverviewTabContent {...props} />
  </ErrorBoundary>
);

export default HROverviewTab;
